// ============================================
// MoltiVerse v3 - Network Flex Card
// Uses Canvas API for pixel-perfect image export
// ============================================

import { useUniverseStore } from '@/stores/universe-store';
import { SUBMOLT_COLORS, type Agent } from '@/lib/types';
import { useMemo, useRef, useState, useCallback, useEffect } from 'react';

interface NetworkFlexCardProps {
  agent: Agent;
  onClose?: () => void;
}

export function NetworkFlexCard({ agent, onClose }: NetworkFlexCardProps) {
  const {
    agentInfluencers,
    agentInfluenced,
    agentConnections,
  } = useUniverseStore();
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copying' | 'copied' | 'error'>('idle');
  
  const stats = useMemo(() => {
    const totalBonds = agentConnections.direct.length;
    const influencerCount = agentInfluencers.length;
    const influencedCount = agentInfluenced.length;
    
    const totalSignals = agentInfluencers.reduce((sum, a) => sum + parseInt(String(a._interactionCount || 0)), 0) +
                         agentInfluenced.reduce((sum, a) => sum + parseInt(String(a._interactionCount || 0)), 0);
    
    const topConnection = [...agentInfluencers, ...agentInfluenced]
      .sort((a, b) => parseInt(String(b._interactionCount || 0)) - parseInt(String(a._interactionCount || 0)))[0];
    
    // Posts count (handle null/undefined/0)
    const postCount = agent.post_count || 0;
    const commentCount = agent.comment_count || 0;
    const totalPosts = postCount + commentCount;
    
    // Karma per post - use total content created
    const karmaPerPost = totalPosts > 0 
      ? Math.round((agent.karma || 0) / totalPosts)
      : agent.karma || 0; // If no posts, show full karma as "efficiency"
    
    // Days active - calculate properly with timezone safety
    const firstSeen = agent.first_seen ? new Date(agent.first_seen) : null;
    const lastActive = agent.last_active ? new Date(agent.last_active) : new Date();
    
    let daysActive = 1;
    if (firstSeen) {
      const msPerDay = 1000 * 60 * 60 * 24;
      const diffMs = lastActive.getTime() - firstSeen.getTime();
      daysActive = Math.max(1, Math.ceil(diffMs / msPerDay)); // Use ceil for more generous count
    }
    
    const karmaPerDay = Math.round((agent.karma || 0) / daysActive);
    
    // Influence multiplier - better formatting
    let influenceMultiplier: string;
    if (influencerCount === 0 && influencedCount === 0) {
      influenceMultiplier = '0';
    } else if (influencerCount === 0) {
      influenceMultiplier = `${influencedCount}`; // Just show the reach count
    } else {
      const ratio = influencedCount / influencerCount;
      influenceMultiplier = ratio >= 10 ? Math.round(ratio).toString() : ratio.toFixed(1);
    }
    
    return {
      totalBonds,
      influencerCount,
      influencedCount,
      totalSignals,
      topConnection,
      karmaPerPost,
      karmaPerDay,
      daysActive,
      influenceMultiplier,
      totalPosts,
    };
  }, [agent, agentInfluencers, agentInfluenced, agentConnections]);

  const submoltColor = SUBMOLT_COLORS[agent.main_submolt] || SUBMOLT_COLORS.default;
  
  const getTier = (karma: number) => {
    if (karma >= 100000) return { name: 'MYTHIC', color: '#ff00ff' };
    if (karma >= 50000) return { name: 'LEGENDARY', color: '#ffd700' };
    if (karma >= 10000) return { name: 'EPIC', color: '#a855f7' };
    if (karma >= 1000) return { name: 'RARE', color: '#3b82f6' };
    if (karma >= 100) return { name: 'UNCOMMON', color: '#22c55e' };
    return { name: 'COMMON', color: '#6b7280' };
  };
  
  const tier = getTier(agent.karma || 0);
  
  // Determine if text should be dark based on background luminance
  const getTextColor = (bgColor: string): string => {
    // Parse hex color
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // Format date helper
  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return '—';
    }
  };

  // Draw the card on canvas
  const drawCard = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const W = 620;
    const H = 300;
    const scale = 2; // Retina quality
    
    canvas.width = W * scale;
    canvas.height = H * scale;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.scale(scale, scale);
    
    // === OUTER GLOW ===
    ctx.shadowColor = tier.color;
    ctx.shadowBlur = 40;
    ctx.fillStyle = '#0a0a12';
    ctx.beginPath();
    ctx.roundRect(4, 4, W - 8, H - 8, 20);
    ctx.fill();
    ctx.shadowBlur = 0;
    
    // === BACKGROUND ===
    const bgGrad = ctx.createLinearGradient(0, 0, W, H);
    bgGrad.addColorStop(0, '#0e0e18');
    bgGrad.addColorStop(0.5, '#0a0a12');
    bgGrad.addColorStop(1, '#08080e');
    ctx.fillStyle = bgGrad;
    ctx.beginPath();
    ctx.roundRect(4, 4, W - 8, H - 8, 18);
    ctx.fill();
    
    // === ANIMATED BORDER EFFECT ===
    // Outer glow border
    ctx.shadowColor = tier.color;
    ctx.shadowBlur = 15;
    ctx.strokeStyle = tier.color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(4, 4, W - 8, H - 8, 18);
    ctx.stroke();
    ctx.shadowBlur = 0;
    
    // Inner subtle border
    ctx.strokeStyle = `${tier.color}40`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(8, 8, W - 16, H - 16, 16);
    ctx.stroke();
    
    // === RIGHT PANEL ===
    ctx.fillStyle = '#050509';
    ctx.beginPath();
    ctx.roundRect(390, 12, W - 402, H - 24, 12);
    ctx.fill();
    
    // Right panel border
    ctx.strokeStyle = '#1a1a28';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(390, 12, W - 402, H - 24, 12);
    ctx.stroke();
    
    // === AVATAR ===
    ctx.shadowColor = submoltColor;
    ctx.shadowBlur = 20;
    ctx.fillStyle = submoltColor;
    ctx.beginPath();
    ctx.roundRect(28, 28, 60, 60, 14);
    ctx.fill();
    ctx.shadowBlur = 0;
    
    // Avatar letter
    ctx.fillStyle = getTextColor(submoltColor);
    ctx.font = 'bold 30px JetBrains Mono, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(agent.name.charAt(0).toUpperCase(), 58, 58);
    
    // === USERNAME ===
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 26px JetBrains Mono, monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`@${agent.name}`, 104, 28);
    
    // === BADGES ===
    const submoltText = `m/${agent.main_submolt}`.toUpperCase();
    ctx.font = 'bold 10px JetBrains Mono, monospace';
    const submoltWidth = ctx.measureText(submoltText).width + 16;
    
    ctx.fillStyle = submoltColor;
    ctx.beginPath();
    ctx.roundRect(104, 64, submoltWidth, 22, 5);
    ctx.fill();
    
    ctx.fillStyle = getTextColor(submoltColor);
    ctx.textBaseline = 'middle';
    ctx.fillText(submoltText, 112, 75);
    
    // Tier badge
    const tierText = tier.name;
    ctx.font = 'bold 10px JetBrains Mono, monospace';
    const tierWidth = ctx.measureText(tierText).width + 16;
    const tierX = 104 + submoltWidth + 8;
    
    ctx.shadowColor = tier.color;
    ctx.shadowBlur = 8;
    ctx.fillStyle = tier.color;
    ctx.beginPath();
    ctx.roundRect(tierX, 64, tierWidth, 22, 5);
    ctx.fill();
    ctx.shadowBlur = 0;
    
    ctx.fillStyle = getTextColor(tier.color);
    ctx.fillText(tierText, tierX + 8, 75);
    
    // === KARMA SECTION ===
    ctx.fillStyle = '#444444';
    ctx.font = 'bold 11px JetBrains Mono, monospace';
    ctx.textBaseline = 'top';
    ctx.fillText('TOTAL KARMA', 28, 115);
    
    // Karma value with glow
    ctx.shadowColor = tier.color;
    ctx.shadowBlur = 15;
    ctx.fillStyle = tier.color;
    ctx.font = 'bold 56px JetBrains Mono, monospace';
    ctx.fillText((agent.karma || 0).toLocaleString(), 28, 135);
    ctx.shadowBlur = 0;
    
    // === MINI STATS ROW ===
    const miniStats = [
      { label: 'DAYS ACTIVE', value: stats.daysActive > 1 ? `${stats.daysActive}` : '< 1', icon: '📅' },
      { label: 'FIRST POST', value: formatDate(agent.first_seen), icon: '🌟' },
      { label: 'LAST ACTIVE', value: formatDate(agent.last_active), icon: '⏰' },
    ];
    
    const miniY = 200; // Moved up from 210 to give more space above footer
    ctx.font = 'bold 9px JetBrains Mono, monospace';
    
    miniStats.forEach((stat, i) => {
      const x = 28 + i * 115;
      
      // Icon and label
      ctx.fillStyle = '#555555';
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      ctx.fillText(`${stat.icon} ${stat.label}`, x, miniY);
      
      // Value
      ctx.fillStyle = '#888888';
      ctx.font = 'bold 14px JetBrains Mono, monospace';
      ctx.fillText(stat.value, x, miniY + 14);
      ctx.font = 'bold 9px JetBrains Mono, monospace';
    });
    
    // === FOOTER ===
    ctx.strokeStyle = '#1a1a28';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(28, H - 52);
    ctx.lineTo(365, H - 52);
    ctx.stroke();
    
    // Footer dot with glow
    ctx.shadowColor = tier.color;
    ctx.shadowBlur = 10;
    ctx.fillStyle = tier.color;
    ctx.beginPath();
    ctx.arc(36, H - 30, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    
    // Footer text - MOLTIVERSE branding
    ctx.fillStyle = '#555555';
    ctx.font = 'bold 11px JetBrains Mono, monospace';
    ctx.textBaseline = 'middle';
    ctx.fillText('MOLTIVERSE', 52, H - 30);
    
    // Credit - Made by @0xprismis
    ctx.fillStyle = '#1d9bf0'; // Twitter blue
    ctx.fillText('•  Made by @0xprismis', 142, H - 30);
    
    // === RIGHT SIDE STATS ===
    const statsData = [
      { icon: '🔗', label: 'BONDS', value: stats.totalBonds.toLocaleString(), color: '#fbbf24' },
      { icon: '⚡', label: 'SIGNALS', value: stats.totalSignals.toLocaleString(), color: '#a855f7' },
      { icon: '📊', label: 'KARMA/POST', value: stats.totalPosts > 0 ? stats.karmaPerPost.toLocaleString() : '—', color: '#06b6d4' },
      { icon: '🚀', label: 'KARMA/DAY', value: stats.karmaPerDay.toLocaleString(), color: '#22c55e' },
      { icon: '📡', label: 'REACH', value: stats.influencedCount.toLocaleString(), color: '#f97316' },
      { icon: '🎯', label: 'INFLUENCE', value: stats.influenceMultiplier === '0' ? '—' : `${stats.influenceMultiplier}x`, color: '#ec4899' },
    ];
    
    // Calculate proper spacing for 6 items in the right panel
    // Right panel is at y=12, height=H-24 (276), so usable height with padding = ~250
    const panelTopPadding = 22; // Start below panel top edge
    const panelBottomPadding = 22;
    const usableHeight = H - panelTopPadding - panelBottomPadding; // ~256
    const rowHeight = usableHeight / statsData.length; // ~42.6px per row
    
    statsData.forEach((stat, i) => {
      const y = panelTopPadding + i * rowHeight + rowHeight / 2; // Center in each row
      
      // Icon
      ctx.font = '16px JetBrains Mono, monospace';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(stat.icon, 404, y);
      
      // Label
      ctx.fillStyle = '#666666';
      ctx.font = 'bold 10px JetBrains Mono, monospace';
      ctx.fillText(stat.label, 426, y);
      
      // Value with glow
      ctx.shadowColor = stat.color;
      ctx.shadowBlur = 6;
      ctx.fillStyle = stat.color;
      ctx.font = 'bold 20px JetBrains Mono, monospace';
      ctx.textAlign = 'right';
      ctx.fillText(stat.value, 598, y);
      ctx.shadowBlur = 0;
      
      ctx.textAlign = 'left';
    });
    
  }, [agent, tier, submoltColor, stats, getTextColor]);

  // Draw on mount and when data changes
  useEffect(() => {
    drawCard();
  }, [drawCard]);

  const handleCopyImage = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    setCopyStatus('copying');
    
    try {
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to create blob'));
        }, 'image/png', 1.0);
      });
      
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        setCopyStatus('copied');
      } catch {
        // Fallback: download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${agent.name}-moltiverse.png`;
        a.click();
        URL.revokeObjectURL(url);
        setCopyStatus('copied');
      }
      
      setTimeout(() => setCopyStatus('idle'), 2500);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  }, [agent.name]);

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="flex flex-col items-center gap-3 sm:gap-4 max-w-full overflow-hidden">
        {/* The Canvas - this IS the image */}
        <canvas 
          ref={canvasRef}
          className="max-w-full h-auto"
          style={{
            borderRadius: '16px',
            boxShadow: `0 0 40px ${tier.color}30`,
          }}
        />

        {/* Action buttons */}
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-center">
          <button
            onClick={handleCopyImage}
            disabled={copyStatus === 'copying'}
            className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50"
            style={{
              background: copyStatus === 'copied' 
                ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.3), rgba(34, 197, 94, 0.2))'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
              border: copyStatus === 'copied' 
                ? '1px solid rgba(34, 197, 94, 0.5)'
                : '1px solid rgba(255, 255, 255, 0.1)',
              color: copyStatus === 'copied' ? '#22c55e' : '#ffffff',
            }}
          >
            {copyStatus === 'copying' ? (
              'Copying...'
            ) : copyStatus === 'copied' ? (
              '✓ Copied!'
            ) : copyStatus === 'error' ? (
              'Failed - Try Again'
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy Image
              </>
            )}
          </button>
          
          <button
            onClick={() => onClose?.()}
            className="px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm text-white/40 hover:text-white/80 transition-all hover:bg-white/5"
          >
            Close
          </button>
        </div>
        
        <p className="text-[10px] sm:text-xs text-white/30 text-center px-4">
          Tap "Copy Image" to copy, then paste anywhere!
        </p>
      </div>
    </div>
  );
}
